import { create } from "zustand";
import { persist } from "zustand/middleware";
import { db } from "@/lib/db";
import { ALL_VOWS, type VowCategory } from "@/data/vows";

interface FavoritesState {
  /** Set of vowIds that are favorited */
  favoriteIds: Set<number>;
  /** Loading flag for initial hydration */
  loaded: boolean;
  /** Initialize from IndexedDB */
  loadFavorites: () => Promise<void>;
  /** Toggle a single vow as favorite */
  toggleFavorite: (vowId: number) => Promise<void>;
  /** Toggle all vows in a category (add all if not all favorited, remove all otherwise) */
  toggleCategoryFavorite: (categoryId: VowCategory) => Promise<void>;
  /** Check if a vow is favorited */
  isFavorite: (vowId: number) => boolean;
  /** Check if ALL vows in a category are favorited */
  isCategoryFavorite: (categoryId: VowCategory) => boolean;
  /** Get favorite vows for display (count items, starting from offset based on slotIndex) */
  getFavoriteVowsForSlot: (slotIndex: number, count: number, order: "sequential" | "random") => typeof ALL_VOWS;
}

export const useFavoritesStore = create<FavoritesState>()(
  persist(
    (set, get) => ({
      favoriteIds: new Set<number>(),
      loaded: false,

      loadFavorites: async () => {
        try {
          const favorites = await db.favorites.toArray();
          const ids = new Set(favorites.map((f) => f.vowId));
          set({ favoriteIds: ids, loaded: true });
        } catch {
          // If IndexedDB fails, use whatever is in localStorage (persist)
          set({ loaded: true });
        }
      },

      toggleFavorite: async (vowId: number) => {
        const { favoriteIds } = get();
        const newFavorites = new Set(favoriteIds);

        if (newFavorites.has(vowId)) {
          newFavorites.delete(vowId);
          try {
            await db.favorites.where("vowId").equals(vowId).delete();
          } catch {
            // Ignore IndexedDB errors
          }
        } else {
          newFavorites.add(vowId);
          try {
            await db.favorites.add({
              vowId,
              createdAt: new Date().toISOString(),
            });
          } catch {
            // Ignore IndexedDB errors
          }
        }

        set({ favoriteIds: newFavorites });
      },

      toggleCategoryFavorite: async (categoryId: VowCategory) => {
        const { favoriteIds } = get();
        const categoryVows = ALL_VOWS.filter((v) => v.category === categoryId);
        const allFavorited = categoryVows.every((v) => favoriteIds.has(v.id));

        const newFavorites = new Set(favoriteIds);

        if (allFavorited) {
          // Remove all vows in category
          for (const vow of categoryVows) {
            newFavorites.delete(vow.id);
            try {
              await db.favorites.where("vowId").equals(vow.id).delete();
            } catch {
              // Ignore
            }
          }
        } else {
          // Add all vows in category that aren't already favorited
          for (const vow of categoryVows) {
            if (!newFavorites.has(vow.id)) {
              newFavorites.add(vow.id);
              try {
                await db.favorites.add({
                  vowId: vow.id,
                  createdAt: new Date().toISOString(),
                });
              } catch {
                // Ignore (may already exist)
              }
            }
          }
        }

        set({ favoriteIds: newFavorites });
      },

      isFavorite: (vowId: number) => {
        return get().favoriteIds.has(vowId);
      },

      isCategoryFavorite: (categoryId: VowCategory) => {
        const { favoriteIds } = get();
        const categoryVows = ALL_VOWS.filter((v) => v.category === categoryId);
        if (categoryVows.length === 0) return false;
        return categoryVows.every((v) => favoriteIds.has(v.id));
      },

      getFavoriteVowsForSlot: (slotIndex: number, count: number, order: "sequential" | "random") => {
        const { favoriteIds } = get();
        const favorites = ALL_VOWS.filter((v) => favoriteIds.has(v.id));
        if (favorites.length === 0) return [];
        const n = Math.min(count, favorites.length);

        let startIdx: number;
        if (order === "random") {
          // Deterministic "random" using slot index + prime for distribution
          startIdx = (slotIndex * 37) % favorites.length;
        } else {
          // Sequential: each slot shows the next batch
          startIdx = ((slotIndex) * n) % favorites.length;
        }

        const result = [];
        for (let i = 0; i < n; i++) {
          result.push(favorites[(startIdx + i) % favorites.length]);
        }
        return result;
      },
    }),
    {
      name: "vajra-favorites",
      // Custom serialization for Set
      storage: {
        getItem: (name) => {
          const raw = localStorage.getItem(name);
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          if (parsed?.state?.favoriteIds) {
            parsed.state.favoriteIds = new Set(parsed.state.favoriteIds);
          }
          return parsed;
        },
        setItem: (name, value) => {
          const serializable = {
            ...value,
            state: {
              ...value.state,
              favoriteIds: Array.from(value.state.favoriteIds),
            },
          };
          localStorage.setItem(name, JSON.stringify(serializable));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
